"use client";

import React from "react";
import { motion, useReducedMotion } from "framer-motion";
import { CalendarRange, ChartLine as LineChart, ShieldAlert, ArrowUpRight, TrendingUp, Sparkles, Building2, Sun } from 'lucide-react';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { Link } from "@/components/common/Link";
const challenges = [{
  id: 1,
  title: "Midweek Corporate vs Weekend Leisure Shifts",
  subtitle: "Demand management",
  iconName: "CalendarRange",
  description: "Balance intense weekday corporate occupancy from Pithampur, Super Corridor, and Vijay Nagar with weekend leisure and transit dips. We institute dynamic day-of-week rate fences and bundled weekend packages to sustain high RevPAR throughout the week.",
  metricLabel: "Strategy focus",
  metricValue: "Day-of-week fencing & corporate length-of-stay controls"
}, {
  id: 2,
  title: "Aggressive Comp-Set Undercutting",
  subtitle: "Real-time rate intelligence",
  iconName: "LineChart",
  description: "Track intraday rate shifts across MakeMyTrip, Agoda, and Booking.com across key Indore micro-markets. Automated competitive alerts and yield adjustments protect your RevPAR while securing high-intent bookings ahead of market drops.",
  metricLabel: "Strategy focus",
  metricValue: "Intraday OTA monitoring & rule-based rate fencing"
}, {
  id: 3,
  title: "The OTA Commission & Direct Booking Gap",
  subtitle: "Channel distribution",
  iconName: "ShieldAlert",
  description: "Stop unauthorized B2B wholesaler leakage and reclaim margins lost to steep OTA commissions. We enforce strict rate parity across meta-search and drive direct corporate and banquet guest conversions through targeted booking incentives.",
  metricLabel: "Strategy focus",
  metricValue: "Wholesaler rate fencing & direct channel boost"
}];
const seasonalityData = [{
  period: "October – March",
  label: "Peak Commercial & Wedding Season",
  icon: Sparkles,
  badge: "High Yield & Maximum ADR",
  description: "High-density corporate conferences, regional trade expos, and lavish wedding dates create heavy compression. Focus shifts to strict minimum length-of-stay controls, premium tier pricing, and non-refundable booking fences."
}, {
  period: "July – September",
  label: "Monsoon & Transit Demand",
  icon: Building2,
  badge: "Steady Corporate & Religious Inflow",
  description: "Continuous corporate movement supported by religious transit to Ujjain and Omkareshwar. Strategy prioritizes business traveler loyalty, MICE packages, and dynamic last-minute flash yield adjustments."
}, {
  period: "April – June",
  label: "Summer Shoulder Period",
  icon: Sun,
  badge: "Occupancy-Driven Yielding",
  description: "Higher daytime temperatures soften transient leisure, making commercial contracts and staycation packaging vital. Dynamic rate flexing and OTA visibility optimization keep base occupancy stable without brand dilution."
}];
export default function Landscape() {
  const shouldReduceMotion = useReducedMotion();
  const containerVariants = {
    hidden: {},
    show: {
      transition: {
        staggerChildren: shouldReduceMotion ? 0 : 0.15
      }
    }
  };
  const cardVariants = {
    hidden: {
      opacity: 0,
      y: shouldReduceMotion ? 0 : 20
    },
    show: {
      opacity: 1,
      y: 0,
      transition: {
        duration: 0.5,
        ease: "easeOut"
      }
    }
  };
  return <section id="hospitalitylandscapeindore" data-nav="light" className="relative bg-background py-20 md:py-28 text-foreground">
      <div className="container mx-auto px-4 sm:px-6 lg:px-8 max-w-7xl">
        {/* Section Header */}
        <div className="mx-auto max-w-3xl text-center mb-16 md:mb-20">
          <Badge variant="outline" className="mb-4 border-secondary/40 text-secondary-foreground">
            Market Dynamics & Yield Protection
          </Badge>
          <h2 className="font-serif text-3xl sm:text-4xl md:text-5xl font-bold tracking-tight text-foreground">
            Navigating the hospitality landscape of Indore
          </h2>
          <p className="mt-4 text-base sm:text-lg text-muted-foreground leading-relaxed">
            Indore stands as Madhya Pradesh&apos;s commercial capital and a booming hospitality hub fueled by rapid corporate growth, Pithampur industrial corridors, healthcare tourism, and high-volume wedding events. Sunil Singh structures disciplined revenue models designed specifically for midscale, upscale, and boutique properties navigating this competitive market.
          </p>
        </div>

        {/* 3-Column Card Grid */}
        <motion.div variants={containerVariants} initial="hidden" whileInView="show" viewport={{
        once: true,
        amount: 0.15
      }} className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6 lg:gap-8">
          {challenges.map((item, index) => <motion.div key={item.id} data-index={index} variants={cardVariants} className="h-full flex">
              <Card className="flex flex-col justify-between w-full border border-border bg-card shadow-sm hover:shadow-md transition-[border-color,box-shadow] duration-300">
                <CardHeader className="gap-3">
                  <div className="flex items-center justify-between">
                    <div className="inline-flex size-12 items-center justify-center rounded-md bg-secondary/10 text-secondary-foreground">
                      {item.iconName === "CalendarRange" && <CalendarRange className="size-6 text-chart-2" />}
                      {item.iconName === "LineChart" && <LineChart className="size-6 text-chart-2" />}
                      {item.iconName === "ShieldAlert" && <ShieldAlert className="size-6 text-chart-2" />}
                    </div>
                    <span className="text-xs font-mono font-medium text-muted-foreground tracking-wider uppercase">
                      0{index + 1}
                    </span>
                  </div>
                  <div>
                    <span className="text-xs font-semibold uppercase tracking-wider text-secondary">
                      {item.subtitle}
                    </span>
                    <CardTitle className="font-serif text-xl sm:text-2xl font-bold mt-1.5 text-foreground leading-snug">
                      {item.title}
                    </CardTitle>
                  </div>
                </CardHeader>

                <CardContent className="space-y-6 pt-2">
                  <CardDescription className="text-sm leading-relaxed text-muted-foreground">
                    {item.description}
                  </CardDescription>

                  <div className="rounded-md border border-border/60 bg-muted/40 p-3.5 text-xs">
                    <p className="font-semibold text-foreground mb-1">
                      {item.metricLabel}
                    </p>
                    <p className="text-muted-foreground">
                      {item.metricValue}
                    </p>
                  </div>
                </CardContent>
              </Card>
            </motion.div>)}
        </motion.div>

        {/* Seasonality Block */}
        <div className="mt-16 md:mt-20 rounded-xl border border-border bg-card p-6 md:p-10 shadow-sm">
          <div className="flex flex-col md:flex-row md:items-center md:justify-between gap-4 pb-8 border-b border-border/60">
            <div>
              <div className="inline-flex items-center gap-2 text-xs font-semibold uppercase tracking-wider text-secondary mb-1">
                <TrendingUp className="size-4" />
                <span>Indore Market Seasonality</span>
              </div>
              <h3 className="font-serif text-2xl md:text-3xl font-bold text-foreground">
                Strategic Yield Dynamics by Season
              </h3>
            </div>
            <p className="text-sm text-muted-foreground md:max-w-md">
              Data-backed pacing models tailored to capture corporate contracts, grand weddings, and regional pilgrimage transit year-round.
            </p>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-3 gap-6 pt-8">
            {seasonalityData.map((season, index) => {
            const Icon = season.icon;
            return <div key={season.period} data-index={index} className="rounded-lg border border-border/60 bg-muted/20 p-5 flex flex-col justify-between">
                  <div>
                    <div className="flex items-center justify-between gap-2 mb-3">
                      <div className="inline-flex size-9 items-center justify-center rounded-md bg-secondary/15 text-secondary-foreground">
                        <Icon className="size-4 text-chart-2" />
                      </div>
                      <Badge variant="outline" className="text-[11px] font-mono border-secondary/30">
                        {season.period}
                      </Badge>
                    </div>
                    <h4 className="font-serif text-lg font-bold text-foreground mb-1">
                      {season.label}
                    </h4>
                    <p className="text-xs font-semibold text-secondary mb-3">
                      {season.badge}
                    </p>
                    <p className="text-xs sm:text-sm text-muted-foreground leading-relaxed">
                      {season.description}
                    </p>
                  </div>
                </div>;
          })}
          </div>
        </div>

        {/* Section Bottom CTA / Footnote */}
        <div className="mt-14 md:mt-18 pt-8 border-t border-border/70 flex flex-col sm:flex-row items-center justify-between gap-4">
          <p className="text-sm text-muted-foreground text-center sm:text-left">
            Need a tailored revenue assessment for your Indore hotel or boutique stay?
          </p>
          <Button asChild variant="default" className="bg-primary text-primary-foreground hover:bg-primary/90">
            <Link to="#" className="inline-flex items-center gap-2">
              <span>Book a Revenue Audit</span>
              <ArrowUpRight className="size-4" />
            </Link>
          </Button>
        </div>
      </div>
    </section>;
}